import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { NodeViewProps } from '@tiptap/react';
import type { MutableRefObject } from 'react';

import { Box, Heading } from '@chakra-ui/react';
import { Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { useMemo } from 'react';

import { formatDocumentName } from '#utils/format-document-name';

import { NP_DOCUMENT_TITLE_TAG } from './markdown-document-title';
import { DocumentHeadingSubpagesPortal } from './tools/document-heading/document-heading-subpages-portal';

interface MarkdownDocumentTitleOptions {
  contextRef: MutableRefObject<MarkdownEditorContextValue>;
}

function MarkdownDocumentTitleNodeView({ extension, selected }: NodeViewProps) {
  const { contextRef } = extension.options as MarkdownDocumentTitleOptions;
  const context = contextRef.current;

  const title = useMemo(() => {
    const path = context.parentPath.trim();
    const labels = formatDocumentName(context.parentPath.trim());
    const lastLabel = labels.length > 0 ? labels[labels.length - 1] : '';
    return lastLabel || path || 'Untitled';
  }, [context.parentPath]);

  return (
    <NodeViewWrapper
      className={
        selected
          ? 'markdown-document-title-node markdown-document-title-node--selected'
          : 'markdown-document-title-node'
      }
      contentEditable={false}
      data-drag-handle={false}
    >
      <Heading
        as="h1"
        size="2xl"
        className="markdown-document-title-node__title"
      >
        {title}
      </Heading>
      <Box mt={1}>
        <DocumentHeadingSubpagesPortal
          parentPath={context.parentPath}
          children={context.childDocuments}
          editorPlaceholderMuted={context.editorPlaceholderMuted}
          isNewDocument={context.isNewDocument}
          onCreateSubpage={context.onCreateSubpage}
          onNavigateToDocument={context.onNavigateToDocument}
          onRequestRename={context.onRequestRename}
        />
      </Box>
    </NodeViewWrapper>
  );
}

export const MarkdownDocumentTitleNode =
  Node.create<MarkdownDocumentTitleOptions>({
    name: 'npDocumentTitle',
    group: 'block',
    atom: true,
    selectable: true,
    isolating: true,
    defining: true,

    addOptions() {
      return {
        contextRef: { current: null as never },
      };
    },

    parseHTML() {
      return [{ tag: 'np-document-title' }];
    },

    renderHTML() {
      return ['np-document-title'];
    },

    renderMarkdown() {
      return NP_DOCUMENT_TITLE_TAG;
    },

    addNodeView() {
      return ReactNodeViewRenderer(MarkdownDocumentTitleNodeView);
    },
  });
